// components/ui/Alert.tsx
import { useState, type ReactNode } from 'react';
import { X, CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';
import { cn } from '../../lib/utils';

type AlertVariant = 'success' | 'warning' | 'error' | 'info';

interface AlertProps {
  children: ReactNode;
  variant?: AlertVariant;
  title?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

const variantStyles: Record<AlertVariant, { bg: string; border: string; text: string; icon: string }> = {
  success: {
    bg: 'bg-success-subtle',
    border: 'border-success/20',
    text: 'text-success-text',
    icon: 'text-success',
  },
  warning: {
    bg: 'bg-warning-subtle',
    border: 'border-warning/20',
    text: 'text-warning-text',
    icon: 'text-warning',
  },
  error: {
    bg: 'bg-error-subtle',
    border: 'border-error/20',
    text: 'text-error-text',
    icon: 'text-error',
  },
  info: {
    bg: 'bg-info-subtle',
    border: 'border-info/20',
    text: 'text-info-text',
    icon: 'text-info',
  },
};

const icons: Record<AlertVariant, typeof CheckCircle> = {
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle,
  info: Info,
};

export function Alert({
  children,
  variant = 'info',
  title,
  dismissible,
  onDismiss,
  className
}: AlertProps) {
  const [visible, setVisible] = useState(true);
  const styles = variantStyles[variant];
  const Icon = icons[variant];

  if (!visible) return null;

  const handleDismiss = () => {
    setVisible(false);
    onDismiss?.();
  };

  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        styles.bg,
        styles.border,
        className
      )}
      role="alert"
    >
      <div className="flex gap-3">
        <Icon className={cn('w-5 h-5 shrink-0 mt-0.5', styles.icon)} />
        <div className="flex-1">
          {title && (
            <h3 className={cn('font-medium text-body-md mb-1', styles.text)}>{title}</h3>
          )}
          <div className={cn('text-body-sm', styles.text)}>{children}</div>
        </div>
        {dismissible && (
          <button
            onClick={handleDismiss}
            className={cn(
              'p-1 rounded-md hover:bg-text/5 transition-colors duration-fast',
              styles.text
            )}
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
